import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';
import trigraphs from '../trigraphs';
import type { ClassificationMark, ClassificationMarkStringArrayKey, MarkFormat } from '../types';
import { LevelValueEnum, MarkTypeEnum } from '../types';
import { messages as parseMessages } from '../parseClassification';

import {
  hasUniqueElements,
  sortedAlphabetically,
  sortedNumeric,
  trigraphsTetragraphSortedAlphabetically,
  trigraphsThenTetragraphs,
  usaFirst,
} from './utils';

const messages = defineMessages({
  invalidFouo: {
    id: 'IsValidClassificationMark.invalidFouo',
    defaultMessage: 'FOUO may only be used with UNCLASSIFIED information.',
  },
  invalidCui: {
    id: 'IsValidClassificationMark.invalidCui',
    defaultMessage: 'CUI may only be used with UNCLASSIFIED information.',
  },
  invalidSbu: {
    id: 'IsValidClassificationMark.invalidSbu',
    defaultMessage: 'SBU may only be used with UNCLASSIFIED information.',
  },
  invalidDcni: {
    id: 'IsValidClassificationMark.invalidDcni',
    defaultMessage: 'DCNI may only be used with UNCLASSIFIED information.',
  },
  unclassifiedImcon: {
    id: 'IsValidClassificationMark.unclassifiedImcon',
    defaultMessage: 'IMCON cannot be used with UNCLASSIFIED marks.',
  },
  unclassifiedAccm: {
    id: 'IsValidClassificationMark.unclassifiedAccm',
    defaultMessage: 'ACCM cannot be used with UNCLASSIFIED marks.',
  },
  unclassifiedOrcon: {
    id: 'IsValidClassificationMark.unclassifiedOrcon',
    defaultMessage: 'ORCON cannot be used with UNCLASSIFIED marks.',
  },
  unclassifiedNoforn: {
    id: 'IsValidClassificationMark.unclassifiedNoforn',
    defaultMessage: 'NOFORN cannot be used with UNCLASSIFIED marks.',
  },
  unclassifiedRelTo: {
    id: 'IsValidClassificationMark.unclassifiedRelTo',
    defaultMessage: 'REL TO cannot be used with UNCLASSIFIED marks.',
  },
  unclassifiedExdis: {
    id: 'IsValidClassificationMark.unclassifiedExdis',
    defaultMessage: 'EXDIS cannot be used with UNCLASSIFIED marks.',
  },
  unclassifiedNodis: {
    id: 'IsValidClassificationMark.unclassifiedNodis',
    defaultMessage: 'NODIS cannot be used with UNCLASSIFIED marks.',
  },
  exdisNodis: {
    id: 'IsValidClassificationMark.exdisNodis',
    defaultMessage: 'EXDIS and NODIS may not be used together.',
  },
  unclassifiedDisplayOnly: {
    id: 'IsValidClassificationMark.unclassifiedDisplayOnly',
    defaultMessage: 'DISPLAY ONLY cannot be used with UNCLASSIFIED marks.',
  },
  unclassifiedRelido: {
    id: 'IsValidClassificationMark.unclassifiedRelido',
    defaultMessage: 'RELIDO cannot be used with UNCLASSIFIED marks.',
  },
  sciDuplicates: {
    id: 'IsValidClassificationMark.sciDuplicates',
    defaultMessage: 'Duplicate SCI markings.',
  },
  sapDuplicates: {
    id: 'IsValidClassificationMark.sapDuplicates',
    defaultMessage: 'Duplicate SAP markings.',
  },
  fgiDuplicates: {
    id: 'IsValidClassificationMark.fgiDuplicates',
    defaultMessage: 'Duplicate FGI markings.',
  },
  relToDuplicates: {
    id: 'IsValidClassificationMark.relToDuplicates',
    defaultMessage: 'Duplicate REL TO markings.',
  },
  displayOnlyDuplicates: {
    id: 'IsValidClassificationMark.displayOnlyDuplicates',
    defaultMessage: 'Duplicate DISPLAY ONLY markings.',
  },
  invalidSciControl: {
    id: 'IsValidClassificationMark.invalidSciControl',
    defaultMessage: 'NOFORN must be used with HCS and/or TK-GEOCAP.',
  },
  invalidNfControl: {
    id: 'IsValidClassificationMark.invalidNfControl',
    defaultMessage: 'NOFORN cannot be used with FGI, REL TO, or RELIDO.',
  },
  invalidRelToControl: {
    id: 'IsValidClassificationMark.invalidRelToControl',
    defaultMessage: 'REL TO cannot be to USA only.',
  },
  invalidRelToCount: {
    id: 'IsValidClassificationMark.invalidRelToCount',
    defaultMessage: 'REL TO must have at least one government entity.',
  },
  invalidAccmCount: {
    id: 'IsValidClassificationMark.invalidAccmCount',
    defaultMessage: 'ACCM must have at least one program specified.',
  },
  invalidDisplayOnlyControl: {
    id: 'IsValidClassificationMark.invalidDisplayOnlyControl',
    defaultMessage: 'DISPLAY ONLY cannot be used with NOFORN or RELIDO.',
  },
  invalidNato: {
    id: 'IsValidClassificationMark.invalidNato',
    defaultMessage: 'NOFORN cannot be used with NATO information.',
  },
  invalidSciSorting: {
    id: 'IsValidClassificationMark.invalidSciSorting',
    defaultMessage: 'SCI controls must be sorted alphabetically.',
  },
  invalidSapSorting: {
    id: 'IsValidClassificationMark.invalidSapSorting',
    defaultMessage: 'SAP controls must be sorted alphabetically.',
  },
  invalidFgiSorting: {
    id: 'IsValidClassificationMark.invalidFgiSorting',
    defaultMessage:
      'FGI order invalid (trigraphs alphabetically, followed by tetragraphs alphabetically).',
  },
  invalidRelToSorting: {
    id: 'IsValidClassificationMark.invalidRelToSorting',
    defaultMessage: 'REL TO control must list entities in alphabetical order, with USA first.',
  },
  invalidRelToEntityOrder: {
    id: 'IsValidClassificationMark.invalidRelToEntityOrder',
    defaultMessage: 'REL TO control must have trigraphs followed by tetragraphs.',
  },
  invalidDisplayOnlySorting: {
    id: 'IsValidClassificationMark.invalidDisplayOnlySorting',
    defaultMessage:
      'DISPLAY ONLY order invalid (trigraphs alphabetically, followed by tetragraphs alphabetically).',
  },
  invalidCnwdiClassification: {
    id: 'IsValidClassificationMark.invalidCnwdiClassification',
    defaultMessage: 'RD-N/FRD-N used with SECRET or TOP SECRET.',
  },
  invalidRdFrdClassification: {
    id: 'IsValidClassificationMark.invalidRdFrdClassification',
    defaultMessage: 'RD and FRD may only be used with CONFIDENTIAL, SECRET, or TOP SECRET.',
  },
  invalidRdFrd: {
    id: 'IsValidClassificationMark.invalidRdFrd',
    defaultMessage: 'RD and FRD may not be used together.',
  },
  invalidSigmaRdFrd: {
    id: 'IsValidClassificationMark.invalidSigmaRdFrd',
    defaultMessage: 'SIGMA must be used with either RD or FRD.',
  },
  invalidSigmaOrdering: {
    id: 'IsValidClassificationMark.invalidSigmaOrdering',
    defaultMessage: 'SIGMA values must be ordered numerically.',
  },
  invalidCnwdi: {
    id: 'IsValidClassificationMark.invalidCnwdi',
    defaultMessage: 'CNWDI must be used with RD or FRD.',
  },
  invalidFgiTrigraphs: {
    id: 'IsValidClassificationMark.invalidFgiTrigraphs',
    defaultMessage: 'FGI has invalid trigraph(s).',
  },
  invalidRelToTrigraphs: {
    id: 'IsValidClassificationMark.invalidRelToTrigraphs',
    defaultMessage: 'REL TO has invalid trigraph(s).',
  },
  invalidDisplayOnlyTrigraphs: {
    id: 'IsValidClassificationMark.invalidDisplayOnlyTrigraphs',
    defaultMessage: 'DISPLAY ONLY has invalid trigraph(s).',
  },
  invalidFgiTriTetraOnly: {
    id: 'IsValidClassificationMark.invalidFgiTriTetraOnly',
    defaultMessage: 'FGI has entities that are not trigraphs or tetragraphs.',
  },
  invalidRelToTriTetraOnly: {
    id: 'IsValidClassificationMark.invalidRelToTriTetraOnly',
    defaultMessage: 'REL TO has entities that are not trigraphs or tetragraphs.',
  },
  invalidDisplayOnlyTriTetraOnly: {
    id: 'IsValidClassificationMark.invalidDisplayOnlyTriTetraOnly',
    defaultMessage: 'DISPLAY ONLY has entities that are not trigraphs or tetragraphs.',
  },
  invalidBannerJointEntityCount: {
    id: 'IsValidClassificationMark.invalidBannerJointEntityCount',
    defaultMessage: 'JOINT control must have at least two government entities.',
  },
  invalidPortionJointEntityCount: {
    id: 'IsValidClassificationMark.invalidPortionJointEntityCount',
    defaultMessage: 'JOINT control cannot have only one government entity.',
  },
  invalidSapClassification: {
    id: 'IsValidClassificationMark.invalidSapClassification',
    defaultMessage: 'SAP control may not be used with UNCLASSIFIED information.',
  },
  invalidSciClassification: {
    id: 'IsValidClassificationMark.invalidSciClassification',
    defaultMessage: 'SCI control may not be used with UNCLASSIFIED information.',
  },
});

type MarkValidator = (a: ClassificationMark) => boolean;

/**
 * Returns true if the mark is CLASSIFIED, or if it is UNCLASSIFIED and does not have the specified property
 */
const validateClassifiedProperty =
  (key: 'oc' | 'imc' | 'displayOnly' | 'rd' | 'frd' | 'sci' | 'sap' | 'accm'): MarkValidator =>
  (mark) => {
    return (
      mark.classificationLevel === undefined ||
      mark.classificationLevel > LevelValueEnum.UNCLASSIFIED ||
      mark[key] === undefined ||
      !mark[key]
    );
  };

/**
 * Returns true if the mark is CLASSIFIED or CUI, or if it is UNCLASSIFIED and does not have the specified property
 */
const validateControlledProperty =
  (key: 'relTo' | 'relido' | 'nf' | 'xd' | 'nd'): MarkValidator =>
  (mark) =>
    mark.classificationLevel === undefined ||
    mark.classificationLevel > LevelValueEnum.UNCLASSIFIED ||
    (mark.classificationLevel === LevelValueEnum.UNCLASSIFIED && (!!mark.cui || !mark[key]));

/**
 * Returns true if the mark is CLASSIFIED, SBU, or CUI, or if it is UNCLASSIFIED and does not have NOFORN
 * (NOFORN can only be used with CLASSIFIED information, CUI, or SBU-NF)
 */
const validateNoforn: MarkValidator = (mark) => {
  const levelUnknown = mark.classificationLevel === undefined;
  const levelHigherThanClassified =
    !levelUnknown && mark.classificationLevel! > LevelValueEnum.UNCLASSIFIED;
  const levelClassified = mark.classificationLevel === LevelValueEnum.UNCLASSIFIED;
  const nofornNotUsed = !mark.nf;
  const sbuUsed = !!mark.sbu;
  const cuiUsed = !!mark.cui;

  return (
    levelUnknown ||
    levelHigherThanClassified ||
    (levelClassified && (nofornNotUsed || sbuUsed || cuiUsed))
  );
};

/**
 * Returns true if the mark has correct FOUO, CUI, DCNI, or SBU usage (unclassified)
 */
const validatedUnclassifiedProperty =
  (key: 'fouo' | 'cui' | 'sbu' | 'dcni'): MarkValidator =>
  (mark) =>
    !mark[key] || (!!mark[key] && mark.classificationLevel === LevelValueEnum.UNCLASSIFIED);

/**
 * Returns true if the EXDIS and NODIS are not used together
 */
const validateExdisNodis: MarkValidator = (mark) => !mark.xd || !mark.nd;

/**
 * Returns true if the mark has unique elements for the given control
 */
const validateDuplicates =
  (key: ClassificationMarkStringArrayKey): MarkValidator =>
  (mark) =>
    mark[key] === undefined || hasUniqueElements(mark[key]);

/**
 * Returns true if the mark has alphabetical elements for the given control
 */
const validateAlphabetical =
  (key: ClassificationMarkStringArrayKey): MarkValidator =>
  (mark) =>
    sortedAlphabetically(mark[key]);

/**
 * Returns true if the mark has alphabetical elements for the given control (trigraphs & tetragraphs checked separately)
 */
const validateTrigraphsTetragraphsAlphabetical =
  (key: ClassificationMarkStringArrayKey): MarkValidator =>
  (mark) =>
    trigraphsTetragraphSortedAlphabetically(mark[key]);

/**
 * Returns true if the mark has alphabetical elements for the REL TO (trigraphs & tetragraphs checked separately, USA first)
 */
const validateRelToAlphabetical: MarkValidator = (mark) =>
  usaFirst(mark.relTo) &&
  trigraphsTetragraphSortedAlphabetically(mark.relTo?.filter((entity) => entity !== 'USA'));

/**
 * Returns true if the control has trigraphs first, then tetragraphs
 */
const validateTrigraphsThenTetragraphs =
  (key: ClassificationMarkStringArrayKey): MarkValidator =>
  (mark) =>
    trigraphsThenTetragraphs(mark[key]);

/**
 * Returns true if the control has no unknown trigraphs
 */
const validateTrigraphs =
  (key: ClassificationMarkStringArrayKey): MarkValidator =>
  (mark) => {
    const triArray = mark[key]?.filter((entity) => entity.length === 3);
    return !triArray?.some((entity) => !(entity in trigraphs));
  };

/**
 * Returns true if all entities in the control are trigraphs or tetragraphs
 */
const validateTriTetraOnly =
  (key: ClassificationMarkStringArrayKey): MarkValidator =>
  (mark) => {
    return !mark[key]?.some((entity) => entity.length < 3 || entity.length > 4);
  };

/**
 * Returns true if the mark meets the specified SCI control requirements (NOFORN used with HCS and TK-GEOCAP)
 */
const validateSci: MarkValidator = (mark) =>
  !(
    (!!mark.sci?.includes('TK-G') || mark.sci?.some((value) => value.startsWith('HCS'))) &&
    !mark.nf
  );

/**
 * Returns true if the mark meets the specified NF control requirements (NF cannot be used with FGI, RELIDO, or REL TO)
 */
const validateNf: MarkValidator = (mark) => {
  const hasRelTo = !!mark.relTo?.length;
  const hasRelido = !!mark.relido;
  const hasFgi = !!mark.fgi?.length;
  const hasNoforn = !!mark.nf;

  return !((hasRelTo || hasRelido || hasFgi) && hasNoforn);
};

/**
 * Returns true if the mark meets the specified REL TO control requirements (REL TO cannot be used with USA only)
 */
const validateRelTo: MarkValidator = (mark) =>
  !(mark.relTo?.includes('USA') && mark.relTo.length === 1);

/**
 * Returns true if the given property has at least one element
 */
const validateAtLeastOneRequired =
  (key: ClassificationMarkStringArrayKey): MarkValidator =>
  (mark) => {
    return mark[key] === undefined || (mark[key] !== undefined && mark[key]!.length > 0);
  };

/**
 * Returns true if the mark meets the specified DISPLAY ONLY control requirements (DISPLAY ONLY cannot be used with NOFORN or RELIDO)
 */
const validateDisplayOnly: MarkValidator = (mark) => {
  const hasRelido = !!mark.relido;
  const hasNoforn = !!mark.nf;
  const hasDisplayOnly = !!mark.displayOnly?.length;

  return !((hasRelido || hasNoforn) && hasDisplayOnly);
};

/**
 * Returns true if the mark meets the specified Joint type rules for banner marks (JOINT must have at least two entities)
 */
const validateJointInBanner: MarkValidator = (mark) =>
  mark.type !== MarkTypeEnum.JOINT ||
  (mark.type === MarkTypeEnum.JOINT && !!mark.fgi && mark.fgi.length >= 2);

/**
 * Returns true if the mark meets the specified Joint type rules for portion marks (JOINT must have zero or 2+ entities)
 */
const validateJointInPortion: MarkValidator = (mark) =>
  mark.type !== MarkTypeEnum.JOINT ||
  mark.fgi === undefined ||
  (mark.type === MarkTypeEnum.JOINT && !!mark.fgi && mark.fgi.length !== 1);

/**
 * Returns true if the mark meets the specified NATO type rules (NOFORN cannot be used with NATO)
 */
const validateNato: MarkValidator = (mark) => !(mark.type === MarkTypeEnum.NATO && mark.nf);

/**
 * Returns true if the mark meets classification level requirements for CNWDI (used with SECRET or TOP SECRET)
 */
const validateCnwdiClassification: MarkValidator = (mark) =>
  !mark.cnwdi ||
  (mark.classificationLevel !== undefined &&
    mark.classificationLevel > LevelValueEnum.CONFIDENTIAL);

/**
 * Returns true if the mark meets classification level requirements for RD/FRD (used with SECRET or TOP SECRET)
 */
const validateRdFrdClassification: MarkValidator = (mark) =>
  !!mark.cnwdi ||
  (mark.classificationLevel !== undefined &&
    mark.classificationLevel >= LevelValueEnum.CONFIDENTIAL) ||
  (!mark.rd && !mark.frd);

/**
 * Returns true if the mark meets the specified RD/FRD control requirements (RD and FRD cannot be used together)
 */
const validateRdFrd: MarkValidator = (mark) => !(mark.rd && mark.frd);

/**
 * Returns true if the mark meets the specified SIGMA RD/FRD control requirements (SIGMA must be used with RD or FRD)
 */
const validateSigmaRdFrd: MarkValidator = (mark) => !mark.sg || !!mark.rd || !!mark.frd;

/**
 * Returns true if the mark meets the specified SIGMA RD/FRD control requirements (SIGMA must be used with RD or FRD)
 */
const validateSigmaOrder: MarkValidator = (mark) => sortedNumeric(mark.sg);

/**
 * Returns true if the mark meets RD/FRD requirements for CNWDI (RD or FRD used with CNWDI)
 */
const validateCnwdi: MarkValidator = (mark) => !mark.cnwdi || !!mark.rd || !!mark.frd;

const bannerMarkOnlyValidators = [
  {
    markValidator: validateAtLeastOneRequired('relTo'),
    errorMessage: messages.invalidRelToCount,
  },
  {
    markValidator: validateJointInBanner,
    errorMessage: messages.invalidBannerJointEntityCount,
  },
  {
    markValidator: validateAtLeastOneRequired('accm'),
    errorMessage: messages.invalidAccmCount,
  },
];

const universalValidators = [
  {
    markValidator: validatedUnclassifiedProperty('fouo'),
    errorMessage: messages.invalidFouo,
  },
  {
    markValidator: validatedUnclassifiedProperty('cui'),
    errorMessage: messages.invalidCui,
  },
  {
    markValidator: validatedUnclassifiedProperty('sbu'),
    errorMessage: messages.invalidSbu,
  },
  {
    markValidator: validatedUnclassifiedProperty('dcni'),
    errorMessage: messages.invalidDcni,
  },
  {
    markValidator: validateClassifiedProperty('imc'),
    errorMessage: messages.unclassifiedImcon,
  },
  {
    markValidator: validateClassifiedProperty('accm'),
    errorMessage: messages.unclassifiedAccm,
  },
  {
    markValidator: validateClassifiedProperty('sap'),
    errorMessage: messages.invalidSapClassification,
  },
  {
    markValidator: validateClassifiedProperty('sci'),
    errorMessage: messages.invalidSciClassification,
  },
  {
    markValidator: validateClassifiedProperty('oc'),
    errorMessage: messages.unclassifiedOrcon,
  },
  {
    markValidator: validateNoforn,
    errorMessage: messages.unclassifiedNoforn,
  },
  {
    markValidator: validateControlledProperty('relTo'),
    errorMessage: messages.unclassifiedRelTo,
  },
  {
    markValidator: validateControlledProperty('xd'),
    errorMessage: messages.unclassifiedExdis,
  },
  {
    markValidator: validateControlledProperty('nd'),
    errorMessage: messages.unclassifiedNodis,
  },
  {
    markValidator: validateExdisNodis,
    errorMessage: messages.exdisNodis,
  },
  {
    markValidator: validateClassifiedProperty('displayOnly'),
    errorMessage: messages.unclassifiedDisplayOnly,
  },
  {
    markValidator: validateControlledProperty('relido'),
    errorMessage: messages.unclassifiedRelido,
  },
  {
    markValidator: validateDuplicates('sci'),
    errorMessage: messages.sciDuplicates,
  },
  {
    markValidator: validateDuplicates('sap'),
    errorMessage: messages.sapDuplicates,
  },
  {
    markValidator: validateDuplicates('fgi'),
    errorMessage: messages.fgiDuplicates,
  },
  {
    markValidator: validateDuplicates('relTo'),
    errorMessage: messages.relToDuplicates,
  },
  {
    markValidator: validateDuplicates('displayOnly'),
    errorMessage: messages.displayOnlyDuplicates,
  },
  {
    markValidator: validateSci,
    errorMessage: messages.invalidSciControl,
  },
  {
    markValidator: validateAlphabetical('sci'),
    errorMessage: messages.invalidSciSorting,
  },
  {
    markValidator: validateAlphabetical('sap'),
    errorMessage: messages.invalidSapSorting,
  },
  {
    markValidator: validateTrigraphsTetragraphsAlphabetical('fgi'),
    errorMessage: messages.invalidFgiSorting,
  },
  {
    markValidator: validateRelToAlphabetical,
    errorMessage: messages.invalidRelToSorting,
  },
  {
    markValidator: validateTrigraphsTetragraphsAlphabetical('displayOnly'),
    errorMessage: messages.invalidDisplayOnlySorting,
  },
  {
    markValidator: validateTrigraphsThenTetragraphs('fgi'),
    errorMessage: messages.invalidFgiSorting,
  },
  {
    markValidator: validateTrigraphsThenTetragraphs('relTo'),
    errorMessage: messages.invalidRelToEntityOrder,
  },
  {
    markValidator: validateTrigraphsThenTetragraphs('displayOnly'),
    errorMessage: messages.invalidDisplayOnlySorting,
  },
  {
    markValidator: validateNf,
    errorMessage: messages.invalidNfControl,
  },
  {
    markValidator: validateRelTo,
    errorMessage: messages.invalidRelToControl,
  },
  {
    markValidator: validateDisplayOnly,
    errorMessage: messages.invalidDisplayOnlyControl,
  },
  {
    markValidator: validateNato,
    errorMessage: messages.invalidNato,
  },
  {
    markValidator: validateRdFrdClassification,
    errorMessage: messages.invalidRdFrdClassification,
  },
  {
    markValidator: validateRdFrd,
    errorMessage: messages.invalidRdFrd,
  },
  {
    markValidator: validateSigmaRdFrd,
    errorMessage: messages.invalidSigmaRdFrd,
  },
  {
    markValidator: validateSigmaOrder,
    errorMessage: messages.invalidSigmaOrdering,
  },
  {
    markValidator: validateCnwdiClassification,
    errorMessage: messages.invalidCnwdiClassification,
  },
  {
    markValidator: validateCnwdi,
    errorMessage: messages.invalidCnwdi,
  },
  {
    markValidator: validateTrigraphs('fgi'),
    errorMessage: messages.invalidFgiTrigraphs,
  },
  {
    markValidator: validateTrigraphs('displayOnly'),
    errorMessage: messages.invalidDisplayOnlyTrigraphs,
  },
  {
    markValidator: validateTrigraphs('relTo'),
    errorMessage: messages.invalidRelToTrigraphs,
  },
  {
    markValidator: validateTriTetraOnly('fgi'),
    errorMessage: messages.invalidFgiTriTetraOnly,
  },
  {
    markValidator: validateTriTetraOnly('displayOnly'),
    errorMessage: messages.invalidDisplayOnlyTriTetraOnly,
  },
  {
    markValidator: validateTriTetraOnly('relTo'),
    errorMessage: messages.invalidRelToTriTetraOnly,
  },
  {
    markValidator: validateJointInPortion,
    errorMessage: messages.invalidPortionJointEntityCount,
  },
];

/**
 * Determines if the given classification mark is valid
 * @param mark the classification mark to validate
 * @param type the format of the mark (banner or portion)
 * @returns a boolean result value, along with an array of issues explaining why the mark is not valid
 */
function isValidClassificationMark(
  mark: ClassificationMark,
  type: MarkFormat
): {
  isValid: boolean;
  issues: Array<MessageDescriptor>;
} {
  // we're uppercasing banner classifications internally, so remove any
  // instances of lowercaseLetters warnings
  if (type === 'banner') {
    mark.warnings = mark.warnings?.filter((warning) => !warning.id?.includes('lowercaseLetters'));
  }

  const issues = [
    ...universalValidators,
    ...(type === 'banner' ? bannerMarkOnlyValidators : []),
  ].reduce((result: Array<MessageDescriptor>, validator) => {
    const valid = validator.markValidator(mark);
    if (!valid) result.push(validator.errorMessage);
    return result;
  }, []);

  const allIssues = [...(mark.warnings ?? []), ...issues];
  const isValid = allIssues.length === 0;

  const filterMalformedError =
    allIssues.length > 1
      ? allIssues.filter((warning) => warning !== parseMessages.malformedMark)
      : allIssues;

  return { isValid, issues: filterMalformedError };
}

export default isValidClassificationMark;
export {
  validatedUnclassifiedProperty,
  validateDuplicates,
  validateSci,
  validateNf,
  validateRelTo,
  validateAtLeastOneRequired,
  validateDisplayOnly,
  validateJointInBanner,
  validateJointInPortion,
  validateNato,
  validateClassifiedProperty,
  validateControlledProperty,
  validateAlphabetical,
  validateTrigraphsThenTetragraphs,
  validateTrigraphsTetragraphsAlphabetical,
  validateRelToAlphabetical,
  validateExdisNodis,
  validateRdFrdClassification,
  validateCnwdiClassification,
  validateRdFrd,
  validateCnwdi,
  validateTrigraphs,
  validateTriTetraOnly,
  validateSigmaRdFrd,
  validateSigmaOrder,
  validateNoforn,
  universalValidators,
  bannerMarkOnlyValidators,
  messages,
};
